/**
 * Excel Import/Export Dialog Component
 * High-performance import/export with progress tracking
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { 
  Upload, Download, FileSpreadsheet, AlertTriangle, CheckCircle2, 
  X, FileDown, FileUp, Loader2, File, Eye, Settings2, ChevronDown,
  History, RotateCcw
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';
import FieldMappingPanel from './FieldMappingPanel';

const ExcelImportExportDialog = ({
  open,
  onOpenChange,
  projectId,
  projectName,
  tasks = [],
  selectedTaskIds = [],
  visibleColumns = [],
  onImportComplete,
  onExportComplete,
}) => {
  const [activeTab, setActiveTab] = useState('export');
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  
  // Export state
  const [exportScope, setExportScope] = useState('all');
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [exportActiveView, setExportActiveView] = useState(false);
  
  // Import state
  const [importFile, setImportFile] = useState(null);
  const [importMode, setImportMode] = useState('replace');
  const [createBaseline, setCreateBaseline] = useState(false);
  const [baselineName, setBaselineName] = useState('');
  const [previewData, setPreviewData] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [importWarnings, setImportWarnings] = useState([]);

  // Field Mapping state
  const [fieldSchema, setFieldSchema] = useState([]);
  const [mappingValue, setMappingValue] = useState({});       // {sourceCol: canonicalKey}
  const [unmappedRequired, setUnmappedRequired] = useState([]);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [exportMappingId, setExportMappingId] = useState('');

  // Export History (audit log + re-download for EPC partners)
  const [exportHistory, setExportHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showMappingUI, setShowMappingUI] = useState(false);

  // Detect file format (used in many places)
  const fileFormat = (() => {
    if (!importFile) return null;
    if (/\.xer$/i.test(importFile.name)) return 'xer';
    if (/\.(mpp|mpx|mpd)$/i.test(importFile.name)) return 'mpp';
    if (/\.xml$/i.test(importFile.name)) return 'mpp';      // MSPDI XML
    return 'excel';
  })();
  
  const fileInputRef = useRef(null);

  // Load Panorix canonical field catalogue + saved templates whenever the
  // dialog opens for a project.
  useEffect(() => {
    if (!open || !projectId) return;
    (async () => {
      try {
        const [schemaResp, tplResp] = await Promise.all([
          api.get('/scheduling/field-schema'),
          api.get(`/scheduling/projects/${projectId}/field-mappings`),
        ]);
        setFieldSchema(schemaResp.data?.fields || []);
        setSavedTemplates(tplResp.data?.mappings || []);
      } catch (err) {
        // Non-fatal — UI degrades gracefully
        console.warn('Field mapping bootstrap failed', err);
      }
    })();
  }, [open, projectId]);

  const refreshTemplates = async () => {
    try {
      const r = await api.get(`/scheduling/projects/${projectId}/field-mappings`);
      setSavedTemplates(r.data?.mappings || []);
    } catch (err) { /* ignore */ }
  };

  // ─── Export History ────────────────────────────────────────────────────
  const fetchExportHistory = useCallback(async () => {
    if (!projectId) return;
    setLoadingHistory(true);
    try {
      const r = await api.get(`/scheduling/projects/${projectId}/export-history?limit=10`);
      setExportHistory(r.data?.exports || []);
    } catch (err) {
      // Non-fatal — degrade quietly
      setExportHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  }, [projectId]);

  // Load history whenever the dialog is opened or History tab is selected
  useEffect(() => {
    if (open && projectId) fetchExportHistory();
  }, [open, projectId, fetchExportHistory]);

  const handleReDownload = async (row) => {
    if (!row?.has_payload) {
      toast.error('This export is metadata-only — please regenerate it from the Export tab.');
      return;
    }
    try {
      const response = await api.get(`/scheduling/exports/${row.id}/download`, { responseType: 'blob' });
      const mimeMap = {
        xer: 'application/octet-stream',
        mpp: 'application/vnd.ms-project',
        mpx: 'application/octet-stream',
        msp_xml: 'application/xml',
      };
      const blob = new Blob([response.data], { type: mimeMap[row.format] || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = row.filename || `export.${row.format}`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Re-downloaded original export');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Re-download failed');
    }
  };

  const formatBytes = (n) => {
    if (!n && n !== 0) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };

  const formatWhen = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString();
    } catch { return iso; }
  };


  const applyAutoMapping = (auto) => {
    if (!auto) {
      setMappingValue({});
      setUnmappedRequired([]);
      return;
    }
    const next = {};
    (auto.mappings || []).forEach((m) => {
      if (m.source_column && m.target_field) {
        next[m.source_column] = m.target_field;
      }
    });
    setMappingValue(next);
    setUnmappedRequired(auto.unmapped_required || []);
  };

  const handleSaveMapping = async (name, isDefault) => {
    try {
      const fmt = fileFormat || 'excel';
      await api.post(`/scheduling/projects/${projectId}/field-mappings`, {
        name,
        direction: 'import',
        format: fmt,
        mapping: mappingValue,
        is_default: !!isDefault,
      });
      toast.success(`Saved mapping "${name}"`);
      await refreshTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save mapping');
    }
  };

  const handleDeleteMapping = async (id) => {
    try {
      await api.delete(`/scheduling/projects/${projectId}/field-mappings/${id}`);
      toast.success('Mapping deleted');
      await refreshTemplates();
    } catch (err) {
      toast.error('Failed to delete mapping');
    }
  };

  const handleLoadTemplate = (tpl) => {
    if (tpl?.mapping) {
      setMappingValue({ ...tpl.mapping });
      toast.success(`Loaded "${tpl.name}"`);
    }
  };

  const handleAutoDetect = async () => {
    const cols = previewData?.columns_detected || [];
    if (!cols.length) {
      toast.error('Pick a file first to detect columns');
      return;
    }
    try {
      const resp = await api.post(
        `/scheduling/projects/${projectId}/field-mappings/auto-detect`,
        { source_columns: cols }
      );
      applyAutoMapping(resp.data);
      toast.success('Auto-mapping refreshed');
    } catch (err) {
      toast.error('Auto-detect failed');
    }
  };
  
  // Handle file selection
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const isXer = /\.xer$/i.test(file.name);
    const isMpp = /\.(mpp|mpx|mpd|xml)$/i.test(file.name);
    const isExcel = /\.(xlsx|xls)$/i.test(file.name);
    if (!isXer && !isMpp && !isExcel) {
      toast.error('Please select an Excel (.xlsx, .xls), Primavera P6 (.xer), or MS Project (.mpp, .mpx, .xml) file');
      return;
    }
    
    // No client-side file-size cap — backend supports background-job
    // preview for files of any size.
    
    setImportFile(file);
    setImportErrors([]);
    setImportWarnings([]);
    setPreviewData(null);
    
    // Get preview from server
    setIsProcessing(true);
    setProgressMessage(
      isXer ? 'Parsing Primavera P6 XER…' :
      isMpp ? 'Parsing MS Project file (mpxj)…' :
              'Uploading and parsing file…'
    );
    setProgress(20);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      // For Excel: kick off async background-job preview to bypass the
      // 100s Cloudflare edge timeout. XER/MPP remain synchronous (their
      // parsers are fast enough).
      let response;
      if (isExcel) {
        const startResp = await api.post(
          `/scheduling/projects/${projectId}/import/preview/start`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 }
        );
        const jobId = startResp.data?.jobId;
        if (!jobId) throw new Error('No job id returned');
        setProgressMessage('Parsing in background — large files may take a moment…');
        // Poll up to 15 minutes
        const deadline = Date.now() + 15 * 60 * 1000;
        let job = null;
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 1500));
          const pollResp = await api.get(
            `/scheduling/projects/${projectId}/import/preview/status/${jobId}`
          );
          job = pollResp.data;
          setProgress((p) => Math.min(95, p + 5));
          if (job?.status === 'done' || job?.status === 'failed') break;
        }
        if (!job || job.status !== 'done') {
          throw new Error(job?.error || 'Preview timed out');
        }
        response = { data: job.result };
      } else {
        const previewUrl = isXer
          ? `/scheduling/projects/${projectId}/import/xer/preview`
          : `/scheduling/projects/${projectId}/import/mpp/preview`;
        response = await api.post(
          previewUrl,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 }
        );
      }
      
      setPreviewData(response.data);
      setImportErrors(response.data.errors || []);
      setImportWarnings(response.data.warnings || []);
      applyAutoMapping(response.data.auto_mapping);
      setShowMappingUI(!isXer && !isMpp);
      setProgress(100);
      
      if (response.data.errors?.length > 0) {
        toast.warning(`Found ${response.data.errors.length} validation errors`);
      } else {
        const parsed = response.data.total_rows ?? response.data.stats?.tasks ?? 0;
        toast.success(`Parsed ${parsed} tasks successfully`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to parse file');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Handle export
  const handleExport = async () => {
    setIsProcessing(true);
    setProgress(10);
    setProgressMessage('Preparing export...');
    
    try {
      // Build query params
      const params = new URLSearchParams();
      params.append('format', exportFormat);
      params.append('scope', exportScope);
      if (exportMappingId) params.append('mapping_id', exportMappingId);
      
      if (exportScope === 'selected' && selectedTaskIds.length > 0) {
        params.append('task_ids', selectedTaskIds.join(','));
      }
      
      if (exportActiveView && visibleColumns.length > 0) {
        params.append('visible_columns', visibleColumns.join(','));
      }
      
      setProgress(30);
      setProgressMessage('Generating file...');
      
      // Server-side export — choose route by selected format
      let exportUrl;
      let downloadExt;
      if (exportFormat === 'xer') {
        exportUrl = `/scheduling/projects/${projectId}/export/xer`;
        downloadExt = 'xer';
      } else if (exportFormat === 'mpp') {
        // MS Project does not document its binary .mpp writer publicly. The
        // industry-standard interchange that modern MS Project opens with
        // full fidelity is MSPDI XML — but EPC partners often expect a
        // .mpp filename. We emit MSPDI XML bytes under a .mpp extension;
        // MS Project handles this transparently on double-click.
        exportUrl = `/scheduling/projects/${projectId}/export/msp?fmt=xml`;
        downloadExt = 'mpp';
      } else if (exportFormat === 'msp_xml') {
        exportUrl = `/scheduling/projects/${projectId}/export/msp?fmt=xml`;
        downloadExt = 'xml';
      } else if (exportFormat === 'mpx') {
        exportUrl = `/scheduling/projects/${projectId}/export/msp?fmt=mpx`;
        downloadExt = 'mpx';
      } else {
        exportUrl = `/scheduling/projects/${projectId}/export/excel?${params.toString()}`;
        downloadExt = exportFormat;
      }
      const response = await api.get(exportUrl, { responseType: 'blob' });
      
      setProgress(90);
      setProgressMessage('Downloading...');
      
      // Download file
      const mimeMap = {
        csv: 'text/csv',
        xer: 'application/octet-stream',
        msp_xml: 'application/xml',
        mpp: 'application/vnd.ms-project',
        mpx: 'application/octet-stream',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      };
      const blob = new Blob([response.data], { type: mimeMap[exportFormat] || mimeMap.xlsx });
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${projectName || 'schedule'}_export.${downloadExt}`;
      link.click();
      URL.revokeObjectURL(url);
      
      setProgress(100);
      toast.success('Export completed successfully');
      onExportComplete?.();
      fetchExportHistory();
      
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Handle import execution
  const handleImport = async () => {
    if (!importFile) {
      toast.error('Please select a file first');
      return;
    }
    
    if (importErrors.length > 0) {
      const proceed = window.confirm(
        `There are ${importErrors.length} validation errors. Import will skip invalid rows. Continue?`
      );
      if (!proceed) return;
    }
    
    setIsProcessing(true);
    setProgress(10);
    setProgressMessage('Uploading file...');
    
    const isXer = /\.xer$/i.test(importFile.name);
    const isMpp = /\.(mpp|mpx|mpd|xml)$/i.test(importFile.name);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      // Attach Field Mapping payload (if user touched it)
      if (mappingValue && Object.keys(mappingValue).length > 0) {
        formData.append('mapping_json', JSON.stringify(mappingValue));
      }

      const params = new URLSearchParams();
      params.append('mode', importMode);
      if (!isXer && !isMpp) {
        params.append('create_baseline', String(createBaseline));
        if (baselineName) params.append('baseline_name', baselineName);
      }
      
      setProgress(30);
      setProgressMessage(
        isXer ? 'Importing Primavera P6 schedule…' :
        isMpp ? 'Importing MS Project schedule…' :
                'Processing import…'
      );
      
      const importUrl = isXer
        ? `/scheduling/projects/${projectId}/import/xer/execute?${params.toString()}`
        : isMpp
        ? `/scheduling/projects/${projectId}/import/mpp/execute?${params.toString()}`
        : `/scheduling/projects/${projectId}/import/execute/start?${params.toString()}`;
      let response;
      if (!isXer && !isMpp) {
        // Async background-job for Excel — no upstream timeout limit
        const startResp = await api.post(
          importUrl,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 600000 }
        );
        const jobId = startResp.data?.jobId;
        if (!jobId) throw new Error('No job id returned');
        setProgressMessage('Importing in background — large files may take several minutes…');
        const deadline = Date.now() + 30 * 60 * 1000;  // up to 30min
        let job = null;
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 2000));
          const pollResp = await api.get(
            `/scheduling/projects/${projectId}/import/preview/status/${jobId}`
          );
          job = pollResp.data;
          setProgress((p) => Math.min(95, p + 3));
          if (job?.status === 'done' || job?.status === 'failed') break;
        }
        if (!job || job.status !== 'done') {
          throw new Error(job?.error || 'Import timed out');
        }
        response = { data: job.result };
      } else {
        response = await api.post(
          importUrl,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 600000,
          }
        );
      }
      
      setProgress(100);
      
      if (response.data.status === 'success' || response.data.success) {
        const count = response.data.tasks_imported ?? response.data.stats?.tasks ?? 0;
        const links = response.data.stats?.dependencies || 0;
        const label = isXer ? 'XER' : isMpp ? 'MS Project' : 'Excel';
        toast.success(`Imported ${count} activities from ${label}${links ? ` (${links} links)` : ''}`);
        onImportComplete?.(response.data);
        onOpenChange(false);
      } else {
        toast.error('Import completed with errors');
      }
      
    } catch (err) {
      // Check if response is a file (error report)
      if (err.response?.data instanceof Blob) {
        const url = URL.createObjectURL(err.response.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'import_error_report.xlsx';
        link.click();
        URL.revokeObjectURL(url);
        toast.error('Import failed. Error report downloaded.');
      } else {
        toast.error(err.response?.data?.detail || 'Import failed');
      }
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Download template (default Panorix import template)
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/scheduling/export/template', {
        responseType: 'blob'
      });
      
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'schedule_import_template.xlsx';
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success('Template downloaded');
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  // Download a pre-filled Excel template from the CURRENT draft mapping
  // (or, if a saved template id is passed, server-side generated from that).
  const handleDownloadMappingTemplate = async (savedId = null) => {
    try {
      let response;
      if (savedId) {
        response = await api.get(
          `/scheduling/projects/${projectId}/field-mappings/${savedId}/template`,
          { responseType: 'blob' }
        );
      } else {
        if (!mappingValue || Object.keys(mappingValue).length === 0) {
          toast.error('Configure at least one mapping first');
          return;
        }
        response = await api.post(
          `/scheduling/projects/${projectId}/field-mappings/template`,
          { mapping: mappingValue, template_name: 'Draft Mapping Template' },
          { responseType: 'blob' }
        );
      }
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = savedId
        ? 'mapping_template.xlsx'
        : 'draft_mapping_template.xlsx';
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Template downloaded — share with your contractor');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Template download failed');
    }
  };
  
  // Reset state when dialog closes
  const handleClose = () => {
    if (isProcessing) {
      const confirm = window.confirm('Processing in progress. Cancel?');
      if (!confirm) return;
    }
    
    setImportFile(null);
    setPreviewData(null);
    setImportErrors([]);
    setImportWarnings([]);
    setProgress(0);
    setProgressMessage('');
    setMappingValue({});
    setUnmappedRequired([]);
    setExportMappingId('');
    setShowMappingUI(false);
    onOpenChange(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-slate-800 border-slate-700 max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="excel-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#1E4E8C]" />
            Excel Import / Export
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Import from or export to Microsoft Excel format
          </DialogDescription>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-slate-900">
            <TabsTrigger value="export" className="data-[state=active]:bg-slate-700" data-testid="export-tab">
              <Download className="w-4 h-4 mr-2" />
              Export
            </TabsTrigger>
            <TabsTrigger value="import" className="data-[state=active]:bg-slate-700" data-testid="import-tab">
              <Upload className="w-4 h-4 mr-2" />
              Import
            </TabsTrigger>
            <TabsTrigger value="history" className="data-[state=active]:bg-slate-700" data-testid="history-tab">
              <History className="w-4 h-4 mr-2" />
              History
            </TabsTrigger>
          </TabsList>
          
          {/* Export Tab */}
          <TabsContent value="export" className="mt-4 space-y-4">
            <div className="space-y-4">
              {/* Export Scope */}
              <div>
                <Label className="text-slate-300 mb-2 block">Export Scope</Label>
                <RadioGroup value={exportScope} onValueChange={setExportScope} className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="all" id="scope-all" />
                    <Label htmlFor="scope-all" className="text-slate-200 cursor-pointer">
                      Export All Tasks ({tasks.length} tasks)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="selected" id="scope-selected" disabled={selectedTaskIds.length === 0} />
                    <Label htmlFor="scope-selected" className={`cursor-pointer ${selectedTaskIds.length === 0 ? 'text-slate-500' : 'text-slate-200'}`}>
                      Export Selected Tasks ({selectedTaskIds.length} selected)
                    </Label>
                  </div>
                </RadioGroup>
              </div>
              
              {/* Export Format */}
              <div>
                <Label className="text-slate-300 mb-2 block">File Format</Label>
                <Select value={exportFormat} onValueChange={setExportFormat}>
                  <SelectTrigger className="bg-slate-900 border-slate-600 w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="xlsx" className="text-slate-200">.xlsx (Excel)</SelectItem>
                    <SelectItem value="csv" className="text-slate-200">.csv (Large datasets)</SelectItem>
                    <SelectItem value="xer" className="text-slate-200">.xer (Primavera P6)</SelectItem>
                    <SelectItem value="mpp" className="text-slate-200">.mpp (MS Project — MSPDI XML)</SelectItem>
                    <SelectItem value="msp_xml" className="text-slate-200">.xml (MS Project — opens natively)</SelectItem>
                    <SelectItem value="mpx" className="text-slate-200">.mpx (MS Project legacy)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Active View Option */}
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="active-view" 
                  checked={exportActiveView}
                  onCheckedChange={setExportActiveView}
                />
                <Label htmlFor="active-view" className="text-slate-200 cursor-pointer">
                  Export visible columns only ({visibleColumns.length} columns)
                </Label>
              </div>

              {/* Saved Mapping (Export) */}
              {savedTemplates.some((t) => t.direction === 'export') && (
                <div>
                  <Label className="text-slate-300 mb-2 block">Apply Saved Mapping (optional)</Label>
                  <Select value={exportMappingId || '__none__'} onValueChange={(v) => setExportMappingId(v === '__none__' ? '' : v)}>
                    <SelectTrigger className="bg-slate-900 border-slate-600" data-testid="export-mapping-select">
                      <SelectValue placeholder="— default columns —" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="__none__" className="text-slate-400 italic">— default columns —</SelectItem>
                      {savedTemplates.filter((t) => t.direction === 'export').map((t) => (
                        <SelectItem key={t.id} value={t.id} className="text-slate-200">
                          {t.name}
                          {t.is_default && <span className="text-emerald-400 text-xs ml-2">[default]</span>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Export Summary */}
              <div className="p-3 bg-slate-900 rounded-lg border border-slate-700">
                <p className="text-sm text-slate-400">Export will include:</p>
                <ul className="mt-2 text-sm text-slate-200 space-y-1">
                  <li>• {exportScope === 'selected' ? selectedTaskIds.length : tasks.length} tasks with WBS hierarchy</li>
                  <li>• Predecessors with dependency types and lag</li>
                  <li>• Resource assignments and calendars</li>
                  <li>• Baseline data (if available)</li>
                  <li>• Constraints, weights, and progress</li>
                </ul>
              </div>
            </div>
          </TabsContent>
          
          {/* Import Tab */}
          <TabsContent value="import" className="mt-4 space-y-4">
            <div className="space-y-4">
              {/* File Upload */}
              <div>
                <Label className="text-slate-300 mb-2 block">Select File — Excel (.xlsx, .xls), Primavera P6 (.xer), or MS Project (.mpp, .mpx, .xml)</Label>
                <div className="flex gap-2">
                  <Input
                    type="file"
                    ref={fileInputRef}
                    accept=".xlsx,.xls,.xer,.mpp,.mpx,.mpd,.xml"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="border-slate-600 flex-1"
                    data-testid="select-file-btn"
                  >
                    <FileUp className="w-4 h-4 mr-2" />
                    {importFile ? importFile.name : 'Select File...'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDownloadTemplate}
                    className="border-slate-600"
                    data-testid="download-template-btn"
                    title="Download Panorix's default import template"
                  >
                    <FileDown className="w-4 h-4 mr-2" />
                    Template
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleDownloadMappingTemplate(null)}
                    className="border-emerald-600 text-emerald-400 hover:bg-emerald-600/10"
                    data-testid="download-mapping-template-btn"
                    title="Generate a pre-filled .xlsx based on your current Field Mapping — share with a contractor before they submit their schedule."
                    disabled={Object.keys(mappingValue).length === 0}
                  >
                    <FileDown className="w-4 h-4 mr-2" />
                    Mapping Template
                  </Button>
                </div>
                {importFile && (
                  <p className="text-xs text-slate-400 mt-1">
                    {(importFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                )}
              </div>
              
              {/* Import Mode */}
              <div>
                <Label className="text-slate-300 mb-2 block">Import Mode</Label>
                <RadioGroup value={importMode} onValueChange={setImportMode} className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="replace" id="mode-replace" />
                    <Label htmlFor="mode-replace" className="text-slate-200 cursor-pointer">
                      Replace entire schedule
                      <span className="text-xs text-red-400 ml-2">(Deletes existing tasks)</span>
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="append" id="mode-append" />
                    <Label htmlFor="mode-append" className="text-slate-200 cursor-pointer">
                      Append to existing schedule
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="update" id="mode-update" />
                    <Label htmlFor="mode-update" className="text-slate-200 cursor-pointer">
                      Update existing tasks
                      <span className="text-xs text-slate-400 ml-2">(Match by Activity ID)</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>
              
              {/* Baseline Option */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox 
                    id="create-baseline" 
                    checked={createBaseline}
                    onCheckedChange={setCreateBaseline}
                  />
                  <Label htmlFor="create-baseline" className="text-slate-200 cursor-pointer">
                    Create baseline from imported BL Start/Finish dates
                  </Label>
                </div>
                {createBaseline && (
                  <Input
                    placeholder="Baseline name (optional)"
                    value={baselineName}
                    onChange={(e) => setBaselineName(e.target.value)}
                    className="bg-slate-900 border-slate-600 mt-2"
                  />
                )}
              </div>
              
              {/* Preview Data */}
              {previewData && (
                <div className="p-3 bg-slate-900 rounded-lg border border-slate-700">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-slate-300 font-medium">Preview</p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="outline" className="border-emerald-500 text-emerald-400" data-testid="preview-rows-badge">
                        {previewData.total_rows || previewData.stats?.tasks || 0} tasks
                      </Badge>
                      {previewData.stats?.summary_tasks > 0 && (
                        <Badge variant="outline" className="border-sky-500 text-sky-400">
                          {previewData.stats.summary_tasks} WBS
                        </Badge>
                      )}
                      {previewData.stats?.calendars > 0 && (
                        <Badge variant="outline" className="border-indigo-500 text-indigo-400">
                          {previewData.stats.calendars} calendars
                        </Badge>
                      )}
                      {previewData.stats?.dependencies > 0 && (
                        <Badge variant="outline" className="border-violet-500 text-violet-400">
                          {previewData.stats.dependencies} dependencies
                        </Badge>
                      )}
                      {previewData.stats?.resources > 0 && (
                        <Badge variant="outline" className="border-cyan-500 text-cyan-400">
                          {previewData.stats.resources} resources
                        </Badge>
                      )}
                      {previewData.stats?.assignments > 0 && (
                        <Badge variant="outline" className="border-fuchsia-500 text-fuchsia-400">
                          {previewData.stats.assignments} assignments
                        </Badge>
                      )}
                      {importErrors.length > 0 && (
                        <Badge variant="outline" className="border-red-500 text-red-400">
                          {importErrors.length} errors
                        </Badge>
                      )}
                      {importWarnings.length > 0 && (
                        <Badge variant="outline" className="border-amber-500 text-amber-400">
                          {importWarnings.length} warnings
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <p className="text-xs text-slate-400 mb-2">
                    Columns: {previewData.columns_detected?.join(', ')}
                  </p>
                  
                  <ScrollArea className="h-32">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-slate-400">
                          <th className="text-left p-1">WBS</th>
                          <th className="text-left p-1">Name</th>
                          <th className="text-left p-1">Duration</th>
                          <th className="text-left p-1">Start</th>
                          <th className="text-left p-1">Finish</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(previewData.preview_rows || previewData.sample_tasks || []).slice(0, 10).map((row, idx) => (
                          <tr key={idx} className="text-slate-200 border-t border-slate-700">
                            <td className="p-1 font-mono">{row.wbs_code || row.wbs || '-'}</td>
                            <td className="p-1 truncate max-w-32">{row.name || '-'}</td>
                            <td className="p-1">{row.duration ?? '-'}</td>
                            <td className="p-1">{row.start ? String(row.start).slice(0, 10) : '-'}</td>
                            <td className="p-1">{row.finish ? String(row.finish).slice(0, 10) : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </ScrollArea>
                </div>
              )}
              
              {/* Field Mapping Toggle + Panel */}
              {previewData?.columns_detected?.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-slate-300">Field Mapping</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowMappingUI((v) => !v)}
                      className="text-slate-400 hover:text-slate-200 h-7 text-xs"
                      data-testid="toggle-mapping-btn"
                    >
                      <Settings2 className="w-3 h-3 mr-1" />
                      {showMappingUI ? 'Hide' : 'Configure'}
                    </Button>
                  </div>
                  {/* MPP / XER files carry calendars, resources and links inside
                      the file itself. Panorix imports them automatically — you
                      do NOT need to map calendar_id, resource_assignments or
                      dependencies for native schedule files. */}
                  {(fileFormat === 'mpp' || fileFormat === 'xer') && showMappingUI && (
                    <div className="rounded-md border border-sky-500/40 bg-sky-500/5 text-xs text-sky-200 p-3 leading-relaxed">
                      <strong className="text-sky-300">Heads-up — MS Project / Primavera P6 imports.</strong>{' '}
                      Calendars, resources and predecessor/successor links travel
                      <em> inside</em> the .{fileFormat} file. Panorix recreates them
                      automatically with fresh internal IDs — you don&apos;t need to
                      map <code className="text-sky-100">calendar_id</code>,
                      {' '}<code className="text-sky-100">resource_assignments</code>{' '}
                      or <code className="text-sky-100">dependencies</code>. Field
                      Mapping below is only needed if a custom textual column
                      should be re-routed to a different Panorix field.
                    </div>
                  )}
                  {showMappingUI && (
                    <FieldMappingPanel
                      columns={previewData.columns_detected || []}
                      fields={fieldSchema}
                      value={mappingValue}
                      onChange={setMappingValue}
                      unmappedRequired={unmappedRequired}
                      savedTemplates={savedTemplates.filter((t) => t.direction === 'import' && (!fileFormat || t.format === fileFormat))}
                      onLoadTemplate={handleLoadTemplate}
                      onSaveTemplate={handleSaveMapping}
                      onDeleteTemplate={handleDeleteMapping}
                      onAutoDetect={handleAutoDetect}
                      onDownloadDraftTemplate={() => handleDownloadMappingTemplate(null)}
                      onDownloadSavedTemplate={(id) => handleDownloadMappingTemplate(id)}
                    />
                  )}
                </div>
              )}

              {/* Errors */}
              {importErrors.length > 0 && (
                <div className="p-3 bg-red-900/20 rounded-lg border border-red-500/50">
                  <p className="text-sm text-red-400 font-medium flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4 h-4" />
                    Validation Errors ({importErrors.length})
                  </p>
                  <ScrollArea className="h-24">
                    <ul className="text-xs text-red-300 space-y-1">
                      {importErrors.slice(0, 10).map((err, idx) => (
                        <li key={idx}>
                          Row {err.row}: {err.field} - {err.error}
                        </li>
                      ))}
                      {importErrors.length > 10 && (
                        <li className="text-red-400">...and {importErrors.length - 10} more errors</li>
                      )}
                    </ul>
                  </ScrollArea>
                </div>
              )}
            </div>
          </TabsContent>

          {/* History Tab — audit log + re-download */}
          <TabsContent value="history" className="mt-4 space-y-3" data-testid="history-content">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-slate-200 text-base">Recent exports</Label>
                <p className="text-xs text-slate-400 mt-1">
                  Last 10 successful exports for this schedule. XER / MPP / MPX / XML
                  files can be re-downloaded byte-for-byte for EPC audits.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchExportHistory}
                disabled={loadingHistory}
                className="border-slate-600 text-slate-200"
                data-testid="history-refresh-btn"
              >
                {loadingHistory ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RotateCcw className="w-4 h-4 mr-2" />
                )}
                Refresh
              </Button>
            </div>

            <ScrollArea className="h-[420px] rounded-lg border border-slate-700 bg-slate-900/60">
              {loadingHistory && exportHistory.length === 0 ? (
                <div className="flex items-center justify-center h-40 text-slate-400 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading history…
                </div>
              ) : exportHistory.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-slate-500 text-sm gap-2">
                  <FileDown className="w-8 h-8 opacity-50" />
                  <span>No exports yet — generate one from the Export tab.</span>
                </div>
              ) : (
                <ul className="divide-y divide-slate-800">
                  {exportHistory.map((row) => (
                    <li
                      key={row.id}
                      className="px-3 py-2.5 flex items-center gap-3 hover:bg-slate-800/40"
                      data-testid={`history-row-${row.id}`}
                    >
                      <div className="shrink-0">
                        <FileDown className="w-5 h-5 text-slate-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-100 text-sm font-medium truncate">
                            {row.filename}
                          </span>
                          <Badge
                            variant="outline"
                            className="text-[10px] uppercase tracking-wide border-slate-600 text-slate-300"
                          >
                            {row.format}
                          </Badge>
                          {!row.has_payload && (
                            <Badge
                              variant="outline"
                              className="text-[10px] border-amber-600/60 text-amber-400"
                              title="Metadata-only — regenerate from Export tab"
                            >
                              metadata
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-slate-400 mt-0.5 truncate">
                          {formatBytes(row.file_size)} ·
                          {' '}{row.task_count ?? 0} tasks ·
                          {' '}{row.exported_by_name || row.exported_by_email || 'unknown'} ·
                          {' '}{formatWhen(row.exported_at)}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleReDownload(row)}
                        disabled={!row.has_payload}
                        className="border-slate-600 text-slate-200 shrink-0"
                        data-testid={`history-redownload-${row.id}`}
                      >
                        <Download className="w-3.5 h-3.5 mr-1.5" />
                        Re-download
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
        
        {/* Progress */}
        {isProcessing && (
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {progressMessage}
              </span>
              <span className="text-slate-300">{progress}%</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        )}
        
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={handleClose} className="border-slate-600" data-testid="cancel-btn">
            {activeTab === 'history' ? 'Close' : 'Cancel'}
          </Button>
          
          {activeTab === 'export' && (
            <Button 
              onClick={handleExport}
              disabled={isProcessing || tasks.length === 0}
              className="bg-[#1E4E8C] hover:bg-[#1E4E8C]/90"
              data-testid="export-btn"
            >
              {isProcessing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export {exportScope === 'selected' ? selectedTaskIds.length : tasks.length} Tasks
            </Button>
          )}
          {activeTab === 'import' && (
            <Button 
              onClick={handleImport}
              disabled={isProcessing || !importFile}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="import-btn"
            >
              {isProcessing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Import {previewData?.total_rows ?? previewData?.stats?.tasks ?? 0} Rows
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExcelImportExportDialog;
